import Grid from '../Grid'
import { useTheme } from '../../utils/useTheme'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

interface ToolbarContainerProps {
  children: ReactNode
  skipMarginOffset?: boolean
}

const ToolbarContainer = ({ children, skipMarginOffset = false }: ToolbarContainerProps): React.ReactElement => {
  const theme = useTheme()
  const [isScrolled, setIsScrolled] = useState(false)
  const scrollTriggerRef = useRef<HTMLDivElement>(null)

  const scrollTrigger = scrollTriggerRef.current
  const handleIntersect = useCallback(([entry]: IntersectionObserverEntry[]) => {
    setIsScrolled(!entry.isIntersecting)
  }, [])

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return
    }

    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '0px',
      threshold: [0]
    })

    if (scrollTrigger != null) {
      observer.observe(scrollTrigger)
    }

    return () => {
      observer.disconnect()
    }
  }, [handleIntersect, scrollTrigger])

  const scrolledProps = {
    boxShadow: theme.elevation.mediumDepth,
    bgcolor: theme.tokens.background.main.default,
    marginLeft: `-${String(theme.spacing(8))}`,
    marginRight: `-${String(theme.spacing(8))}`,
    paddingLeft: theme.spacing(8),
    paddingRight: theme.spacing(8),
    paddingBottom: theme.spacing(3)
  }

  return (
    <>
      <Grid
        ref={scrollTriggerRef}
        {...(!skipMarginOffset && {
          marginTop: `-${String(theme.spacing(2))}`,
          marginBottom: `-${String(theme.spacing(2))}`
        })}
      />
      <Grid
        zIndex={theme.zIndex.toolbar}
        position="sticky"
        top={`-${String(theme.spacing(8))}`}
        {...(isScrolled && scrolledProps)}
      >
        {children}
      </Grid>
    </>
  )
}

export default ToolbarContainer

export { ToolbarContainer as DashboardFrameToolbarContainer }
